<template>
  <div class="attend-page">
    <van-nav-bar left-arrow @click-left="goBack" fixed placeholder>
      <template #title>
        <div class="nav-title">{{ pageTitle }}</div>
      </template>
    </van-nav-bar>

    <!-- 统计信息 -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-num">{{ students.length }}</span>
        <span class="stat-label">总人数</span>
      </div>
      <div class="stat-item present">
        <span class="stat-num">{{ presentCount }}</span>
        <span class="stat-label">
          {{ attendanceStatus === 0 ? "已勾选" : "已签到" }}
        </span>
      </div>
      <div class="stat-item absent">
        <span class="stat-num">{{ absentCount }}</span>
        <span class="stat-label">
          {{ attendanceStatus === 0 ? "未勾选" : "未签到" }}
        </span>
      </div>
    </div>

    <!-- 提示 -->
    <div class="attend-status-bar" v-if="attendanceStatus == 1 && isSelf == 1">
      <van-icon name="passed" color="#07c160" />
      <span>已签到（点击学生可修改状态）</span>
    </div>
    <div
      class="attend-status-bar pending"
      v-else-if="attendanceStatus == 0 && isSelf == 1"
    >
      <van-icon name="clock-o" color="#ff976a" />
      <span>未签到（勾选已到学生后提交）</span>
    </div>
    <div class="attend-status-bar other" v-else-if="attendanceStatus == 1 && isSelf != 1">
      <van-icon name="info-o" color="#52C1B2" />
      <span>{{ otherName }}已签到，暂不可修改</span>
    </div>

    <!-- 全选按钮（仅未签到状态显示） -->
    <div
      class="select-all-bar"
      v-if="attendanceStatus === 0 && isSelf == 1 && students.length > 0"
    >
      <van-button
        size="small"
        :type="isAllSelected ? 'default' : 'primary'"
        plain
        @click="toggleSelectAll"
      >
        {{ isAllSelected ? "取消全选" : "全选" }}
      </van-button>
    </div>

    <!-- 学生列表 -->
    <div class="student-list" v-if="students.length > 0">
      <div
        class="student-item"
        v-for="student in students"
        :key="student.student_id"
        :class="{ 'status-absent': student.status === 0 }"
      >
        <div
          class="checkbox"
          @click="toggleAttendance(student)"
          :class="{ checked: student.status === 1 }"
        >
          <van-icon v-if="student.status === 1" name="success" color="#fff" size="14" />
        </div>
        <div class="student-info">
          <div class="student-name">
            {{ student.name }}
            <span v-if="student.is_leave == 1" class="leave-tag">请假</span>
          </div>
          <div class="student-class">{{ student.class_name }}</div>
        </div>

        <!-- 场景 A：如果是未签到状态 (第一次签到) -->
        <div class="status-tag" v-if="attendanceStatus == 0">
          <span v-if="student.status === 1" class="tag-draft">确认出勤</span>
          <span v-else class="tag-draft pending">未签到</span>
        </div>

        <!-- 场景 B：如果是已签到状态 (修改状态) -->
        <div class="status-tag" v-else>
          <span v-if="student.status == 1" class="tag-final">已签到</span>
        </div>

        <!-- 请假按钮 -->
        <!-- <van-button
          size="mini"
          type="warning"
          plain
          class="leave-btn"
          @click.stop="goLeaveApply(student)"
          >请假</van-button
        > -->
      </div>
    </div>

    <van-empty v-else description="暂无学生数据" />

    <!-- 底部操作按钮 -->
    <div class="bottom-action">
      <van-button
        v-if="attendanceStatus === 0 && isSelf == 1"
        type="primary"
        block
        @click="handleSubmit"
        :disabled="submitting || refreshing"
      >
        提交签到
      </van-button>
      <van-button
        v-else-if="attendanceStatus == 1 && isSelf == 1"
        type="warning"
        block
        @click="handleCancel"
        :disabled="submitting || refreshing"
      >
        取消签到
      </van-button>
      <van-button v-else type="default" block disabled>
        {{ otherName }}已签到
      </van-button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";

export default {
  setup() {
    const router = useRouter();
    const route = useRoute();

    const students = ref([]);
    const attendanceStatus = ref(0); // 0=未签到, 1=已签到
    const isSelf = ref(0); // 是否自己发起的
    const otherName = ref("");
    const submitting = ref(false);
    // 正在提交的单学生 id（null=空闲）：列表项是 div 没有 disabled，只能靠锁拦
    const signingId = ref(null);
    // 正在向服务端重查考勤状态：这期间按钮也要锁住，否则会重复提交
    const refreshing = ref(false);

    // 从路由获取参数
    const courseId = route.query.courseId;
    const serviceClassId = route.query.serviceClassId;
    const serviceTimeId = route.query.serviceTimeId;
    const date = route.query.date;
    const type = route.query.type || 1; // 1=正常签到, 3=补签
    const schoolId = route.query.schoolId;
    const title = route.query.title;

    const pageTitle = computed(() => {
      return `${date || ""}  ${title || ""}`;
    });

    const presentCount = computed(
      () => students.value.filter((s) => s.status === 1).length
    );
    const absentCount = computed(
      () => students.value.filter((s) => s.status === 0).length
    );
    // 是否全选（全部已签到）
    const isAllSelected = computed(
      () => students.value.length > 0 && students.value.every((s) => s.status === 1)
    );

    // 获取学生考勤状态（返回是否成功，供调用方决定要不要弹提示）
    const getAttendanceStatus = async () => {
      refreshing.value = true;
      try {
        Toast.loading({ message: "加载中...", forbidClick: true });
        const res = await api.getAttendanceStatus({
          sid: localStorage.getItem("sid"),
          teacher_id: sessionStorage.getItem("userId"),
          teacher_type: sessionStorage.getItem("role"),
          attendance_date: date,
          course_id: courseId,
          service_time_id: serviceTimeId,
          service_class_id: serviceClassId,
        });
        Toast.clear();
        if (res.data.code == 1001) {
          const result = res.data.result;
          // attendance_status == 0 时默认所有人未签到
          let list = (result.student_list || []).map((student) => ({
            ...student,
            status: result.attendance_status == 0 ? 0 : student.status,
          }));
          students.value = list;
          attendanceStatus.value = result.attendance_status;
          isSelf.value = result.is_self;
          otherName.value = result.other_name || "";
          return true;
        }
        Toast(res.data.message);
        return false;
      } catch (error) {
        Toast.clear();
        Toast("获取考勤状态失败");
        return false;
      } finally {
        refreshing.value = false;
      }
    };

    // 忙时拦截：请求中/重查中一律拦下并给提示，静默 return 会让用户以为按钮坏了
    const checkBusy = () => {
      if (submitting.value || signingId.value || refreshing.value) {
        Toast("处理中，请稍候");
        return true;
      }
      return false;
    };

    // 全选/取消全选
    const toggleSelectAll = () => {
      if (attendanceStatus.value !== 0 || isSelf.value != 1) return;
      if (checkBusy()) return;
      const newStatus = isAllSelected.value ? 0 : 1;
      students.value.forEach((s) => (s.status = newStatus));
    };

    // 切换学生签到状态
    const toggleAttendance = (student) => {
      if (checkBusy()) return;
      if (attendanceStatus.value == 1 && isSelf.value == 1) {
        // 已签到状态 → 弹确认框，调用单学生接口
        const currentStatus = student.status;
        const newStatusText = currentStatus == 1 ? "未签到" : "已签到";
        Dialog.alert({
          title: "确认修改",
          message: `确定要将学生 ${student.name} 的考勤状态修改为${newStatusText}？`,
          showCancelButton: true,
        })
          .then(() => {
            submitSingleStudent(student.student_id, currentStatus == 0);
          })
          .catch(() => {});
      } else if (attendanceStatus.value === 0 && isSelf.value == 1) {
        // 未签到 → 请假生标记到场需二次确认（仅提醒，不改 is_leave）
        if (student.is_leave == 1 && student.status === 0) {
          Dialog.confirm({
            title: "该生已请假",
            message: `确定将 ${student.name} 标记为出勤吗？`,
          })
            .then(() => {
              student.status = 1;
            })
            .catch(() => {});
          return;
        }
        student.status = student.status === 0 ? 1 : 0;
      }
    };

    // 提交单个学生考勤修改
    const submitSingleStudent = async (studentId, isAttend) => {
      if (checkBusy()) return;
      // 锁住这个学生，直到重查结束（只锁到请求回来不够：重查期间列表还能点）
      signingId.value = studentId;
      try {
        // 单学生改动没有按钮可绑 loading，用 Toast 给个反馈（不遮罩，避免挡住返回）
        Toast.loading({ message: "处理中...", forbidClick: false });
        let typeValue = isAttend ? 1 : 2;
        if (type == 3) {
          typeValue = typeValue == 2 ? 2 : 3;
        }
        //1正常签到 2取消签到 3补签
        const res = await api.addStudentAttendance({
          teacher_id: sessionStorage.getItem("userId"),
          course_type: sessionStorage.getItem("role"), //确定是传这个值,接口端会根据角色做相应的处理
          school_id: schoolId,
          attendance_date: date,
          service_class_id: serviceClassId,
          course_id: courseId,
          service_time_id: serviceTimeId,
          type: typeValue,
          student_id: studentId,
        });

        if (res.data.code == 1001) {
          // 乐观更新：先改这一行，避免重查回来之前还能重复点
          const target = students.value.find((s) => s.student_id == studentId);
          if (target) target.status = isAttend ? 1 : 0;
          // 重查完成后再提示，否则 Toast 会被重查的 loading 顶掉
          if (await getAttendanceStatus()) Toast("修改成功");
        } else {
          Toast(res.data.message);
        }
      } catch (error) {
        Toast("操作失败");
      } finally {
        signingId.value = null;
      }
    };

    // 提交签到（全班）
    const handleSubmit = () => {
      if (presentCount.value === 0) {
        Toast("请至少勾选一名学生");
        return;
      }
      const leavePresent = students.value.some((s) => s.is_leave == 1 && s.status === 1);
      const msg = leavePresent ? "有学生已请假，是否确认出勤？" : "确定要进行签到吗？";
      Dialog.alert({
        title: "确认签到",
        message: msg,
        showCancelButton: true,
      })
        .then(() => {
          submitOrCancelAttendance(false);
        })
        .catch(() => {});
    };

    // 取消签到
    const handleCancel = () => {
      Dialog.alert({
        title: "取消签到",
        message: "取消签到后全部孩子出勤状态将被取消，确定要取消签到吗？",
        showCancelButton: true,
      })
        .then(() => {
          submitOrCancelAttendance(true);
        })
        .catch(() => {});
    };

    // 提交或取消 全班考勤
    const submitOrCancelAttendance = async (isCancel) => {
      if (checkBusy()) return;
      try {
        submitting.value = true;
        let typeValue = type;
        if (isCancel) typeValue = 2;

        // 收集缺勤学生ID
        const absentIds = students.value
          .filter((s) => s.status === 0)
          .map((s) => s.student_id)
          .join(",");
        console.log("schoolId", schoolId);
        const res = await api.addAttendance({
          sid: localStorage.getItem("sid"),
          teacher_id: sessionStorage.getItem("userId"),
          teacher_type: sessionStorage.getItem("role"),
          school_id: schoolId,
          attendance_date: date,
          service_class_id: serviceClassId,
          course_id: courseId,
          service_time_id: serviceTimeId,
          type: typeValue,
          student_ids: absentIds,
        });
        if (res.data.code == 1001) {
          // 乐观更新：先把底部按钮切到结果态（提交→已签到、取消→未签到），
          // 否则重查回来之前的这段时间按钮还是旧值、可以再点一次
          attendanceStatus.value = isCancel ? 0 : 1;
          const msg = res.data.message || (isCancel ? "已取消签到" : "签到成功");
          if (await getAttendanceStatus()) Toast.success(msg);
        } else {
          Toast(res.data.message);
        }
      } catch (error) {
        Toast("操作失败");
      } finally {
        submitting.value = false;
      }
    };

    const goBack = () => router.back();

    // 跳转请假申请页
    const goLeaveApply = (student) => {
      sessionStorage.setItem("childId", student.student_id);
      sessionStorage.setItem("childName", student.name);
      router.push("/leaveApply");
    };

    onMounted(() => {
      getAttendanceStatus();
    });

    return {
      students,
      attendanceStatus,
      isSelf,
      otherName,
      submitting,
      refreshing,
      pageTitle,
      presentCount,
      absentCount,
      isAllSelected,
      toggleSelectAll,
      toggleAttendance,
      handleSubmit,
      handleCancel,
      goBack,
      goLeaveApply,
    };
  },
};
</script>

<style lang="less" scoped>
.attend-page {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 80px;
}

.nav-title {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  word-break: break-all;
  white-space: normal;
  line-height: 1.4;
  text-align: center;
  padding: 0 8px;
}

.stats-bar {
  display: flex;
  background: #fff;
  padding: 15px;
  margin-bottom: 1px;
}

.stat-item {
  flex: 1;
  text-align: center;

  .stat-num {
    display: block;
    font-size: 22px;
    font-weight: 700;
    color: #333;
  }

  .stat-label {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  &.present .stat-num {
    color: #07c160;
  }

  &.absent .stat-num {
    color: #ee0a24;
  }
}

.attend-status-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 15px;
  font-size: 13px;
  color: #07c160;
  background: #fff;
  margin-bottom: 10px;

  &.pending {
    color: #ff976a;
  }

  &.other {
    color: var(--color-primary);
  }
}

.student-list {
  padding: 0 10px;
}

.select-all-bar {
  display: flex;
  justify-content: flex-end;
  padding: 8px 15px;
  background: #fff;
}

.student-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;

  &.status-absent {
    background: #fff5f5;
  }
}

.checkbox {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #dcdee0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;

  &.checked {
    background: #07c160;
    border-color: #07c160;
  }
}

.student-info {
  flex: 1;
}

.student-name {
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

.student-class {
  font-size: 12px;
  color: #999;
  margin-top: 3px;
  word-break: break-all;
  white-space: normal;
}

.leave-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  color: #ff976a;
  background: #fff0e8;
  vertical-align: middle;
}

.status-tag {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;

  &.tag-present {
    background: #e8f8ef;
    color: #07c160;
  }

  &.tag-absent {
    background: #fff0f0;
    color: #ee0a24;
  }
}

.bottom-action {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 15px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.leave-btn {
  margin-left: 8px;
  flex-shrink: 0;
}

/* 场景一：草稿模式的样式 (温和、中性) */
.tag-draft {
  color: var(--color-primary); /* 青绿代表待处理 */
  background: var(--color-primary-light);
}
.tag-draft.pending {
  color: #ff976a; /* 橙色代表待确认缺勤 */
  background: #fff0e8;
}

/* 场景二：最终/修改模式的样式 (高对比度) */
.tag-final {
  color: #07c160; /* 绿色代表安全/完成 */
  background: #e8f8ef;
}
.tag-warning {
  color: #ee0a24; /* 红色代表危险/撤销 */
  background: #fff0f0;
  font-weight: bold;
}
</style>
